import React, { useEffect, useState } from "react";
import { OrderDocument, OrderDocumentField } from "@/models";
import { documentFormApi } from "@/services/api";
import { associationApi } from "@/services/api";
import { File, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StorageImage, StorageLink } from "@/components/shared/StorageFile";
import { downloadStorageFile, isImageFile, openStorageFile } from "@/utils/storageRefs";
import { normalizeOrderDocumentFields } from "@/utils/orderDocumentFields";
import { orderApplicantApi } from "@/services/api/orderApplicantApi";

interface DocumentDetailProps {
  document: OrderDocument;
  associationName?: string;
  ipAddress?: string | null;
}

export const DocumentDetail: React.FC<DocumentDetailProps> = ({
  document: rawDocument,
  associationName, // keep for prop compatibility, but do not use for display
  ipAddress,
}) => {
  // Normalize fields shape once so the rest of the render path is shape-agnostic.
  // See src/utils/orderDocumentFields.ts for why this is needed.
  const document = React.useMemo<OrderDocument>(
    () => ({
      ...rawDocument,
      fields: normalizeOrderDocumentFields(rawDocument?.fields as any[]),
    }),
    [rawDocument]
  );

  const [formData, setFormData] = useState<any>(null);
  const [isFormLoading, setIsFormLoading] = useState(true);
  // Applicant roster, so signature values can be attributed to a person.
  // Keyed by applicant_uid (D14). Empty for legacy single-signer orders.
  const [applicantsByUid, setApplicantsByUid] = useState<
    Record<string, { name: string; email: string | null; index: number }>
  >({});
  const [assocName, setAssocName] = useState<string | null>(null);
  const [assocLoading, setAssocLoading] = useState<boolean>(true);

  useEffect(() => {
    // Fetch document form data to get proper field labels
    if (document.document_id) {
      documentFormApi
        .list({
          queryKey: ["document-forms", { document_id: document.document_id }],
        })
        .then((data) => {
          if (data && data.length > 0) {
            console.log("Document form data fetched:", data[0]);
            setFormData(data[0]);
          }
        })
        .catch((error) => {
          console.error("Error fetching document form:", error);
        })
        .finally(() => {
          setIsFormLoading(false);
        });
    } else {
      setIsFormLoading(false);
    }
  }, [document.document_id]);

  useEffect(() => {
    let isMounted = true;
    setAssocLoading(true);
    associationApi.getById(document.association_id)
      .then((assoc) => {
        if (isMounted) setAssocName(assoc?.name || null);
      })
      .catch(() => {
        if (isMounted) setAssocName(null);
      })
      .finally(() => {
        if (isMounted) setAssocLoading(false);
      });
    return () => { isMounted = false; };
  }, [document.association_id]);

  // Load the applicant roster so signature/initials values can be attributed to
  // a named person. Signature values carry applicant_uid (D14); legacy
  // single-signer values do not, in which case this stays empty and the display
  // falls back to exactly today's behaviour.
  useEffect(() => {
    const orderId = document?.order_id;
    if (!orderId) return;

    let cancelled = false;

    (async () => {
      try {
        const rows = await orderApplicantApi.list(orderId);
        if (cancelled || !Array.isArray(rows)) return;

        const map: Record<
          string,
          { name: string; email: string | null; index: number }
        > = {};
        for (const r of rows) {
          if (!r?.applicant_uid) continue;
          map[r.applicant_uid] = {
            name:
              r.name ||
              [r.first_name, r.last_name].filter(Boolean).join(" ") ||
              `Applicant ${(r.applicant_index ?? 0) + 1}`,
            email: r.email ?? null,
            index: r.applicant_index ?? 0,
          };
        }
        setApplicantsByUid(map);
      } catch (e) {
        // Non-fatal: without the roster we simply show no applicant label.
        console.warn("Could not load applicant roster:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [document?.order_id]);

  /** Label a signature value with its signer, when known. */
  const getApplicantLabel = (field: OrderDocumentField): string | null => {
    const uid = field.applicant_uid;

    if (uid) {
      const a = applicantsByUid[uid];
      return a ? `Applicant ${a.index + 1} — ${a.name}` : null;
    }

    // Unattributed value: signatures captured during normal form filling go
    // through the ordinary order PATCH, which knows nothing about applicants, so
    // they carry no applicant_uid. They belong to the person who filled the form
    // — the main applicant — so credit them here rather than showing the
    // signature with no attribution at all.
    //
    // Only when a roster actually exists; legacy single-signer orders keep
    // rendering exactly as before, with no applicant heading.
    const main = Object.values(applicantsByUid).find((a) => a.index === 0);
    return main ? `Applicant 1 — ${main.name}` : null;
  };

  useEffect(() => {
    // Log document fields structure for debugging
    console.log("Document received:", document);
    console.log("Document updated_at value:", document.updated_at);

    if (document.fields) {
      console.log("Document fields:", document.fields);
      const indices = [
        ...new Set(document.fields.map((f) => f.index || 0)),
      ].sort();
      console.log("All field indices in document.fields:", indices);
      console.log(
        "Sample fields by index:",
        indices.map((index) => ({
          index,
          sampleFields: document.fields
            ?.filter((f) => (f.index || 0) === index)
            .slice(0, 3),
        }))
      );

      // Group by section_id for better understanding
      const sectionGroups = document.fields.reduce((acc, field) => {
        const sectionId = field.section_id || 0;
        if (!acc[sectionId]) {
          acc[sectionId] = [];
        }
        acc[sectionId].push(field);
        return acc;
      }, {} as Record<string | number, any[]>);

      console.log(
        "Fields grouped by section_id:",
        Object.keys(sectionGroups).map((sectionId) => ({
          sectionId,
          count: sectionGroups[sectionId].length,
          uniqueIndices: [
            ...new Set(sectionGroups[sectionId].map((f) => f.index || 0)),
          ].sort(),
        }))
      );
    }

    // Log sections and their order values to debug sorting issues
    if (document.sections) {
      console.log(
        "Sections before sorting:",
        document.sections.map((s) => ({
          title: s.title,
          order: s.order,
          section_id: s.section_id,
        }))
      );
    }

    if (formData) {
      console.log("Document form data:", formData);
    }
  }, [document, formData]);

  // Get section title from document form if available
  const getSectionTitle = (sectionId: number | undefined): string => {
    if (!sectionId) return "Unknown Section";

    // Try to find section info from document form data
    if (formData?.form_steps) {
      for (const step of formData.form_steps) {
        if (step.form_sections) {
          for (const section of step.form_sections) {
            if (section.id === sectionId) {
              return section.section_title || `Section ${sectionId}`;
            }
          }
        }
      }
    }

    // Fallback to section_title from the first available field in that section
    if (document.fields) {
      const fieldInSection = document.fields.find(
        (f) => f.section_id === sectionId && f.section_title
      );
      if (fieldInSection && fieldInSection.section_title) {
        return fieldInSection.section_title;
      }
    }

    // Fallback to document sections data
    if (document.sections) {
      const docSection = document.sections.find(
        (s) => s.section_id === sectionId
      );
      if (docSection && docSection.title) {
        return docSection.title;
      }
    }

    return `Section ${sectionId}`;
  };

  // Get field label from document form data
  const getFieldLabel = (
    fieldId: number | undefined,
    sectionId: number | undefined
  ): string => {
    if (!fieldId) return "Unknown Field";

    // First try to find label from document form data
    if (formData?.form_steps) {
      for (const step of formData.form_steps) {
        if (step.form_sections) {
          for (const section of step.form_sections) {
            if (section.id === sectionId && section.form_fields) {
              const field = section.form_fields.find((f) => f.id === fieldId);
              if (field && field.label) {
                return field.label;
              }
            }
          }
        }
      }
    }

    // Fallback to field_title from document.fields
    if (document.fields) {
      const docField = document.fields.find(
        (f) =>
          f.field_id === fieldId && f.section_id === sectionId && f.field_title
      );
      if (docField && docField.field_title) {
        return docField.field_title;
      }
    }

    return `Field ${fieldId}`;
  };

  // Get field type from document form data
  const getFieldType = (
    field: OrderDocumentField,
    sectionId: number | undefined
  ): string => {
    // First priority: field_type from the document fields (jsonb column)
    if (field.field_type) {
      return field.field_type;
    }

    // Second priority: field type from document form data
    const fieldId = field.field_id;
    if (formData?.form_steps) {
      for (const step of formData.form_steps) {
        if (step.form_sections) {
          for (const section of step.form_sections) {
            if (section.id === sectionId && section.form_fields) {
              const formField = section.form_fields.find(
                (f) => f.id === fieldId
              );
              if (formField && formField.field_type) {
                return formField.field_type;
              }
            }
          }
        }
      }
    }

    // Third priority: fallback to checking field ID string for common patterns
    if (!field.field_id) {
      console.log("Field with missing field_id:", field.field_name);
      return "text";
    }
    const fieldIdStr = field.field_id.toString();
    if (fieldIdStr.includes("signature")) return "signature";
    if (fieldIdStr.includes("initials")) return "initials";
    if (fieldIdStr.includes("file")) return "file";

    // Last resort: default to text
    return "text";
  };

  // By extension, ignoring any query string (signed links end in ?token=…).
  const isImageUrl = (url: string): boolean => isImageFile(url);

  const formatDateValue = (fieldName: string, value: any): string => {
    if (!value) return "N/A";

    if (fieldName.toLowerCase().includes("date")) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (error) {
        console.error("Error formatting date:", error);
      }
    }
    return String(value);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return `Electronically signed: ${date.toLocaleString()}`;
      }
    } catch (error) {
      console.error("Error formatting timestamp:", error);
    }
    return "";
  };

  // Get sections organized by steps with proper ordering
  const getOrderedSections = () => {
    if (!formData || !formData.form_steps) {
      // If we don't have form data yet, use the document's sections as fallback
      return (
        document.sections?.slice().sort((a, b) => {
          const orderA = a.order ?? 0;
          const orderB = b.order ?? 0;
          return orderA - orderB;
        }) || []
      );
    }

    // Create a map to quickly look up sections from the form definition
    const formSectionsMap = new Map<number, any>();
    const stepOrderMap = new Map<number, number>();
    formData.form_steps.forEach((step: any) => {
      (step.form_sections || []).forEach((section: any) => {
        formSectionsMap.set(section.id, section);
        stepOrderMap.set(section.id, step.step_order ?? 0);
      });
    });

    // Create a map to quickly look up sections from document
    const documentSectionsMap = new Map(
      (document.sections || []).map((section) => [section.section_id, section])
    );

    // Get all unique section IDs from all sources
    const allSectionIds = new Set<number>();
    formSectionsMap.forEach((_, id) => allSectionIds.add(id));
    documentSectionsMap.forEach((_, id) => allSectionIds.add(id));
    (document.fields || []).forEach((field) => {
      if (field.section_id) allSectionIds.add(field.section_id);
    });

    const sections: any[] = [];
    allSectionIds.forEach((sectionId) => {
      const formSection = formSectionsMap.get(sectionId);
      const docSection = documentSectionsMap.get(sectionId);
      const hasFields = (document.fields || []).some(
        (f) => f.section_id === sectionId
      );

      const fieldWithTitle = (document.fields || []).find(
        (f) => f.section_id === sectionId && f.section_title
      );
      const titleFromFields = fieldWithTitle?.section_title;

      if (formSection || (docSection && hasFields) || hasFields) {
        sections.push({
          ...(docSection || {}),
          section_id: sectionId,
          title:
            formSection?.section_title ||
            titleFromFields ||
            docSection?.title ||
            `Section ${sectionId}`,
          order: formSection?.section_order,
          step_order: stepOrderMap.get(sectionId),
          formSectionId: formSection?.id,
          formSectionData: formSection,
        });
      }
    });

    // Sort sections: those in the form come first, ordered, then others.
    sections.sort((a, b) => {
      const aInForm = a.step_order !== undefined && a.order !== undefined;
      const bInForm = b.step_order !== undefined && b.order !== undefined;

      if (aInForm && bInForm) {
        if (a.step_order !== b.step_order) {
          return (a.step_order || 0) - (b.step_order || 0);
        }
        return (a.order || 0) - (b.order || 0);
      }
      if (aInForm) return -1;
      if (bInForm) return 1;
      return 0; // Keep original order for sections not in form
    });

    return sections;
  };

  // Get ordered fields for a specific section
  const getOrderedFields = (sectionId: number | undefined, index: number) => {
    if (!sectionId) return [];

    // Get fields for this section and index from document.fields
    let sectionFields = (document.fields || []).filter(
      (field) => field.section_id === sectionId && (field.index || 0) === index
    );

    // Find form section data to get field ordering
    let formSectionData: any = null;
    if (formData?.form_steps) {
      for (const step of formData.form_steps) {
        if (step.form_sections) {
          formSectionData = step.form_sections.find(
            (section) => section.id === sectionId
          );
          if (formSectionData) break;
        }
      }
    }

    if (formSectionData?.form_fields) {
      // Create a map of field_id to field_order from the form data
      const fieldOrderMap = new Map(
        formSectionData.form_fields.map((field: any) => [
          field.id,
          typeof field.field_order === "number" ? field.field_order : 0,
        ])
      );

      // Sort fields based on the order from the form data
      sectionFields.sort((a, b) => {
        const orderA = fieldOrderMap.get(a.field_id) ?? 999;
        const orderB = fieldOrderMap.get(b.field_id) ?? 999;
        // Ensure we're comparing numbers for arithmetic operations
        const numOrderA = typeof orderA === "number" ? orderA : 999;
        const numOrderB = typeof orderB === "number" ? orderB : 999;
        return numOrderA - numOrderB;
      });
    }

    return sectionFields;
  };

  const orderedSections = getOrderedSections();

  if (isFormLoading || assocLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (orderedSections.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>This document doesn't have any sections or fields.</p>
      </div>
    );
  }

  // Use document's updated_at property directly
  const documentTimestamp = document.updated_at
    ? String(document.updated_at)
    : "";
  console.log("Document timestamp being used for display:", documentTimestamp);

  return (
    <div className="space-y-8">
      {/* Association Name Section */}
      <div className="bg-brand-orange text-center border border-brand-sage rounded-lg p-4 shadow-md flex items-center justify-center">
        <p className="text-white my-auto -mt-[5px]">
          <span className="font-bold">
            {assocLoading ? "Loading association..." : assocName ? assocName : `Association ID: ${document.association_id}`}
          </span>
        </p>
      </div>
      {/* Uploaded File Section */}
      {document.uploaded_file_path && (
        <div className="border rounded-md p-6 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <File className="h-6 w-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-medium text-blue-900">
                  Uploaded Document
                </h3>
                <p className="text-sm text-blue-700">
                  The requestor has uploaded their own completed form for this
                  document.
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className="bg-blue-100 text-blue-800 border-blue-300"
            >
              File Upload
            </Badge>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void openStorageFile(document.uploaded_file_path)}
              className="border-blue-300 text-blue-700 hover:bg-blue-100"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Uploaded File
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void downloadStorageFile(
                  document.uploaded_file_path,
                  `${document.document?.name || "document"}_uploaded`
                )
              }
              className="border-blue-300 text-blue-700 hover:bg-blue-100"
            >
              <Download className="h-4 w-4 mr-2" />
              Download File
            </Button>
          </div>

          {/* Show a note that form data below might be empty */}
          <div className="mt-4 p-3 bg-blue-100 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Since a file was uploaded, the form
              sections below may be empty or incomplete. The uploaded file above
              contains the completed document.
            </p>
          </div>
        </div>
      )}

      {orderedSections.map((section, idx) => {
        // Get all fields for this section from document.fields
        const sectionFields =
          document.fields?.filter(
            (field) => field.section_id === section.section_id
          ) || [];
        const uniqueIndices = [
          ...new Set(sectionFields.map((f) => f.index || 0)),
        ].sort();

        console.log(
          `Section ${getSectionTitle(section.section_id)} (ID: ${
            section.section_id
          }) - Fields count: ${sectionFields.length}, Unique indices:`,
          uniqueIndices
        );

        if (
          sectionFields.length === 0 &&
          (!section.fields || section.fields.length === 0)
        ) {
          return null; // Skip sections with no fields
        }

        // Skip sections that contain only `information`-type fields (display-
        // only blocks like Terms text). Otherwise the section header renders
        // with no field rows underneath.
        const hasNonInformationField = sectionFields.some(
          (f) => getFieldType(f, section.section_id) !== "information"
        );
        if (!hasNonInformationField) {
          return null;
        }

        return (
          <div
            key={`section-${section.section_id}-${idx}`}
            className="border rounded-md p-6"
          >
            <h3 className="text-xl font-medium mb-4 pb-2 border-b">
              {getSectionTitle(section.section_id)}
            </h3>

            {uniqueIndices.map((index) => {
              // Get ordered fields for this index
              const fieldsForIndex = getOrderedFields(
                section.section_id,
                index
              );

              if (fieldsForIndex.length === 0) return null;

              return (
                <div
                  key={`entry-${section.section_id}-${index}`}
                  className="mb-6 last:mb-0"
                >
                  {/* Only show the Entry header if this section has multiple indices */}
                  {uniqueIndices.length > 1 && (
                    <div className="text-lg font-medium text-gray-700 mb-4">
                      Entry {index + 1}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {fieldsForIndex.map((field, fieldIdx) => {
                      // Get field label and type
                      const fieldLabel = getFieldLabel(
                        field.field_id,
                        field.section_id
                      );
                      const fieldType = getFieldType(field, field.section_id);

                      // `information` fields are display-only blocks (e.g. legal
                      // text, instructions) defined in form_fields.content. They
                      // carry no user value, so skip them in the backoffice viewer
                      // — otherwise they render as "N/A".
                      if (fieldType === "information") return null;

                      // Check if it's a special field type that needs special rendering
                      const isSignature = fieldType === "signature";
                      const isInitials = fieldType === "initials";
                      const isFile = fieldType === "file";

                      // For signature fields, log debugging info
                      if (isSignature || isInitials) {
                        console.log(`Rendering ${fieldType} field:`, {
                          fieldId: field.field_id,
                          value: field.value ? "Has value" : "No value",
                          documentTimestamp: documentTimestamp,
                        });
                      }

                      return (
                        <div
                          key={`field-${section.section_id}-${index}-${fieldIdx}`}
                          className="space-y-2"
                        >
                          <label className="font-medium text-gray-700 block">
                            {fieldLabel}
                          </label>

                          {isSignature || isInitials ? (
                            <div>
                              {/* Attribute the signature to its signer (D14). */}
                              {getApplicantLabel(field) && (
                                <div className="text-xs font-medium text-gray-600 mb-1">
                                  {getApplicantLabel(field)}
                                </div>
                              )}
                              {field.value ? (
                                <div className="flex flex-col">
                                  <StorageImage
                                    src={field.value}
                                    alt={fieldLabel}
                                    className="max-w-[200px] border rounded shadow bg-white"
                                  />
                                  <span className="text-xs text-gray-500 mt-1">
                                    {formatTimestamp(documentTimestamp)}
                                  </span>
                                </div>
                              ) : (
                                <div className="border rounded shadow bg-white p-4 w-[200px] h-[80px] flex items-center justify-center text-gray-400">
                                  No {isSignature ? "signature" : "initials"}{" "}
                                  provided
                                </div>
                              )}
                            </div>
                          ) : isFile && field.value ? (
                            isImageUrl(field.value) ? (
                              <div>
                                <StorageImage
                                  src={field.value}
                                  alt={fieldLabel}
                                  className="max-w-[200px] border rounded shadow"
                                />
                              </div>
                            ) : (
                              <StorageLink
                                href={field.value}
                                className="text-blue-600 underline"
                              >
                                View Attachment
                              </StorageLink>
                            )
                          ) : (
                            <p className="text-gray-900">
                              {formatDateValue(fieldLabel, field.value)}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
